import { useEffect, useMemo } from 'react';

import { generatePath, useNavigate } from 'react-router-dom';
import { B2B_SUB_SYSTEM_SHORT_CODE, B2C_SUB_SYSTEM_SHORT_CODE, ROUTES, mapServerError } from 'core';
import {
  mapToCandidateDetailsRequest,
  mapToCandidateProductDetailsRequest,
  mapToGroupAwardsRequest,
} from 'store/candidate/candidateHelpers';
import { registrationCreated } from 'store/actions';
import { selectBasketExams } from 'store/basket/basketSlice';
import { selectCandidate, selectCandidateProductDetails } from 'store/candidate/candidateSlice';
import { selectPreparationCentre, selectSelectedCountryId } from 'store/country/countrySlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useCreateRegistrationMutation } from 'store/terms-and-conditions/termsAPI';

import { ServerError } from '@britishcouncil/react-forms';

export const useCreateRegistrationSubmit = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const countryId = useAppSelector(selectSelectedCountryId);
  const pcInfo = useAppSelector(selectPreparationCentre);
  const exams = useAppSelector(selectBasketExams);
  const candidateDetails = useAppSelector(selectCandidate);
  const candidateProductDetails = useAppSelector(selectCandidateProductDetails);

  const [createRegistration, createRegistrationData] = useCreateRegistrationMutation();

  const submit = () => {
    if (candidateDetails && candidateProductDetails && exams) {
      createRegistration({
        countryId: Number(countryId),
        candidateDetails: mapToCandidateDetailsRequest(candidateDetails),
        productDetails: mapToCandidateProductDetailsRequest({
          countryId,
          exams,
          candidateProductDetails: candidateProductDetails,
          candidateId: candidateDetails.id,
          pcInfo: pcInfo,
        }),
        registeredExams: exams,
        groupAwards: mapToGroupAwardsRequest(candidateProductDetails.groupAwards),
        subSystemShortCode: pcInfo ? B2B_SUB_SYSTEM_SHORT_CODE : B2C_SUB_SYSTEM_SHORT_CODE,
        nativeLanguage: candidateDetails.nativeLanguage,
      });
    }
  };

  const serverErrors = useMemo<ServerError[]>(
    () => (!createRegistrationData.isError ? [] : mapServerError(createRegistrationData.error)),
    [createRegistrationData]
  );

  useEffect(() => {
    if (createRegistrationData.isSuccess) {
      dispatch(registrationCreated());
      navigate(generatePath(ROUTES.SELECT_PAYMENT, { referenceNumber: createRegistrationData.data }));
    }
  }, [createRegistrationData, dispatch, navigate]);

  return {
    submit,
    serverErrors,
    isSubmitting: createRegistrationData.isLoading,
  };
};
